import { useEffect, useMemo, useState } from "react"
import Icon from "../../components/Icon"
import { SkeletonTable } from "../../components/Skeleton"
import { formatDateWithTime, formatMoney } from "../../utils/format"
import { getApiErrorMessage } from "../../utils/errorMessage"
import { computeUtilityStatementRunningBalances, getUtilityStatementDate } from "../frp/frpStatements"
import Modal from "./Modal"
import StatementTable from "./StatementTable"
import { getCustomerInitials } from "./utbillsHelpers"
import {
  getUtilityRowChargeAmount,
  getUtilityRowPaymentAmount,
  getUtilitySettlementId,
  getVisibleUtilitySettlementRows,
} from "./utilitySettlement"

// A long-standing customer can have hundreds of records. Open on the newest
// ones and let the reader walk back through the history a page at a time.
const PAGE_SIZE = 10

const ROW_TYPE_LABELS = {
  invoice: "Bill",
  payment: "Payment",
  waiver: "Waiver",
  reversal: "Reversal",
}

function HeadlineFigure({ label, value, tone }) {
  const tones = {
    slate: "text-slate-900",
    rose: "text-rose-600",
    emerald: "text-emerald-700",
    teal: "text-teal-700",
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
      <p className="text-[9px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-0.5 text-sm font-black leading-tight tabular-nums ${tones[tone] || tones.slate}`}>
        {formatMoney(value)}
      </p>
    </div>
  )
}

// One customer's complete history with one billing company: every bill, payment
// and waiver, newest first, with the balance they owed after each record.
function CustomerStatementModal({ providerId, providerName, customer, onLoadStatement, onClose }) {
  const [statement, setStatement] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  const customerId = customer?.customerId

  useEffect(() => {
    let active = true
    setLoading(true)
    setError("")
    setVisibleCount(PAGE_SIZE)
    onLoadStatement(providerId, customerId)
      .then((data) => {
        if (active) setStatement(data)
      })
      .catch((err) => {
        if (active) setError(getApiErrorMessage(err, "Failed to load this statement."))
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [providerId, customerId, onLoadStatement])

  // Balances are carried backwards from the closing balance, so the whole
  // history is calculated before it is cut down to the newest records.
  const rowsWithBalance = useMemo(() => {
    const rows = getVisibleUtilitySettlementRows(
      Array.isArray(statement?.rows) ? statement.rows : [],
    )
    return computeUtilityStatementRunningBalances(rows, Number(statement?.netDebtBalance || 0))
  }, [statement])

  const shownRows = rowsWithBalance.slice(0, visibleCount)
  const hiddenCount = rowsWithBalance.length - shownRows.length
  const displayName = statement?.customer?.name || customer?.name || "Customer"
  const contact = statement?.customer?.phone || customer?.phone || customer?.idNumber || ""
  const amountDue = Number(statement?.amountDue || 0)
  const creditAmount = Number(statement?.creditAmount || 0)

  return (
    <Modal
      title={displayName}
      subtitle={[providerName, contact].filter(Boolean).join(" · ")}
      icon="file-earmark-text"
      size="wide"
      onClose={onClose}
    >
      {error && (
        <p className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
          {error}
        </p>
      )}

      {loading ? (
        <SkeletonTable rows={6} columns={5} />
      ) : !statement ? (
        !error && <p className="py-8 text-center text-sm text-slate-500">This statement could not be opened.</p>
      ) : (
        <>
          <div className="flex items-center gap-3 rounded-2xl border border-teal-100 bg-gradient-to-r from-teal-50 to-emerald-50 px-3 py-2.5">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-600 text-xs font-black text-white">
              {getCustomerInitials(displayName)}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[9px] font-bold uppercase tracking-wide text-slate-500">
                {creditAmount > 0 ? "Account credit" : "Balance due"}
              </p>
              <p
                className={`text-xl font-black leading-tight tabular-nums ${
                  amountDue > 0 ? "text-rose-600" : "text-emerald-700"
                }`}
              >
                {formatMoney(creditAmount > 0 ? creditAmount : amountDue)}
              </p>
            </div>
            <span className="shrink-0 rounded-full bg-white px-2.5 py-1 text-[10px] font-bold text-slate-500 ring-1 ring-slate-200">
              {rowsWithBalance.length} record{rowsWithBalance.length === 1 ? "" : "s"}
            </span>
          </div>

          <div className="mt-3 grid grid-cols-3 gap-2">
            <HeadlineFigure label="Billed" value={statement?.summary?.totalInvoiceAmount} tone="slate" />
            <HeadlineFigure label="Paid" value={statement?.summary?.cashPaidAmount} tone="emerald" />
            <HeadlineFigure label="Waived" value={statement?.summary?.totalWaivedAmount} tone="teal" />
          </div>

          <div className="mt-3 overflow-hidden rounded-xl">
            <StatementTable
              rows={shownRows}
              rowKey={(row, index) => `${row.type}-${getUtilitySettlementId(row) || row.invoiceId || index}-${row.date || index}`}
              emptyText="No records yet."
              columns={[
                {
                  label: "Date",
                  width: "19%",
                  render: (row) => formatDateWithTime(getUtilityStatementDate(row)),
                },
                {
                  label: "Description",
                  align: "left",
                  render: (row) => (
                    <div>
                      <div className="font-medium text-slate-900 whitespace-pre-line">{row.description || "Utility record"}</div>
                      <div className="mt-0.5 text-slate-400">
                        {ROW_TYPE_LABELS[row.type] || row.type}
                      </div>
                    </div>
                  ),
                },
                {
                  label: "Invoice",
                  width: "15%",
                  align: "right",
                  render: (row) =>
                    getUtilityRowChargeAmount(row) > 0 ? (
                      <span className="font-semibold text-red-600">{formatMoney(getUtilityRowChargeAmount(row))}</span>
                    ) : (
                      <span className="text-slate-400">-</span>
                    ),
                },
                {
                  label: "Payment",
                  width: "15%",
                  align: "right",
                  render: (row) =>
                    getUtilityRowPaymentAmount(row) > 0 ? (
                      <span className="font-semibold text-emerald-700">{formatMoney(getUtilityRowPaymentAmount(row))}</span>
                    ) : (
                      <span className="text-slate-400">-</span>
                    ),
                },
                {
                  label: "Balance",
                  width: "16%",
                  align: "right",
                  render: (row) => (
                    <span className="font-semibold text-slate-700">{formatMoney(row.runningBalance || 0)}</span>
                  ),
                },
              ]}
            />
          </div>

          {(hiddenCount > 0 || visibleCount > PAGE_SIZE) && (
            <div className="mt-3 flex items-center justify-center gap-2">
              {hiddenCount > 0 && (
                <button
                  type="button"
                  onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-white px-3 py-1.5 text-xs font-bold text-teal-700 transition-colors hover:bg-teal-50"
                >
                  <Icon name="chevron-down" size="0.65rem" />
                  See {Math.min(PAGE_SIZE, hiddenCount)} more
                </button>
              )}
              {visibleCount > PAGE_SIZE && (
                <button
                  type="button"
                  onClick={() => setVisibleCount(PAGE_SIZE)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-600 transition-colors hover:bg-slate-100"
                >
                  <Icon name="chevron-up" size="0.65rem" />
                  Show less
                </button>
              )}
            </div>
          )}
        </>
      )}
    </Modal>
  )
}

export default CustomerStatementModal
